import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import VendorLayout from "@/components/vendor_portal/VendorLayout";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Plus, Package, Share2, Archive, FileText } from "lucide-react";

const TABS = [
  { key: "active", label: "Active" },
  { key: "drafts", label: "Drafts" },
  { key: "shared", label: "Shared" },
  { key: "archived", label: "Archived" },
];

const CATALOG_TYPE_FALLBACK = [
  "Seasonal Collection", "Core Products", "New Arrivals", "Men's", "Women's",
  "Outerwear", "Headwear", "Accessories", "Equipment", "Gifts",
  "Custom Collection", "Other",
];

export default function VendorCatalogsPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("active");
  const [catalogs, setCatalogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [catalogTypes, setCatalogTypes] = useState(CATALOG_TYPE_FALLBACK);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/vendor/catalogs");
      setCatalogs(data || []);
    } catch (_e) {
      setCatalogs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get("/vendor/catalog-options")
      .then(({ data }) => { if (Array.isArray(data?.catalog_types)) setCatalogTypes(data.catalog_types); })
      .catch(() => {});
  }, []);

  const filtered = catalogs.filter((c) => {
    if (tab === "active") return c.status === "active" || c.status === "shared";
    if (tab === "drafts") return c.status === "draft";
    if (tab === "shared") return c.status === "shared";
    if (tab === "archived") return c.status === "archived";
    return true;
  });

  return (
    <VendorLayout>
      <section data-testid="vendor-catalogs-page">
        <div className="flex items-start justify-between flex-wrap gap-6">
          <div>
            <div className="overline">Catalogs</div>
            <h1 className="font-serif text-4xl sm:text-5xl text-[#1A422D] mt-2">Product Catalogs</h1>
            <p className="mt-2 text-[#4A4A4A] max-w-2xl text-sm">
              Create catalogs and share them with approved golf shops. Golf shops choose which products reach their members.
            </p>
          </div>
          <button
            data-testid="vendor-create-catalog-btn"
            onClick={() => setShowCreate(true)}
            className="btn-primary inline-flex items-center gap-2"
          >
            <Plus size={16} /> Create Catalog
          </button>
        </div>

        <div className="mt-8 flex gap-2 border-b border-[#E5E3D8]" data-testid="vendor-catalog-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`vendor-tab-${t.key}`}
              className={`px-4 py-3 text-[11px] uppercase tracking-[0.24em] font-semibold border-b-2 -mb-px transition-colors ${
                tab === t.key ? "border-[#1A422D] text-[#1A422D]" : "border-transparent text-[#7A7A7A] hover:text-[#1A422D]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="mt-10 text-sm text-[#7A7A7A]">Loading catalogs…</div>
        ) : filtered.length === 0 ? (
          <div className="mt-10 p-10 border border-dashed border-[#B59A5A]/40 text-center" data-testid="vendor-catalogs-empty">
            <Package className="mx-auto text-[#B59A5A]" size={28} />
            <div className="mt-3 font-serif text-2xl text-[#1A422D]">No catalogs here yet</div>
            <p className="text-sm text-[#4A4A4A] mt-2">
              {tab === "drafts" ? "Create your first catalog to get started." : "Nothing in this bucket."}
            </p>
          </div>
        ) : (
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="vendor-catalogs-list">
            {filtered.map((c) => (
              <Link
                key={c.catalog_id}
                to={`/vendor/catalogs/${c.catalog_id}`}
                data-testid={`vendor-catalog-card-${c.catalog_id}`}
                className="p-5 border border-[#E5E3D8] bg-white hover:border-[#B59A5A] transition-colors"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="text-[10px] uppercase tracking-[0.24em] font-semibold text-[#B59A5A]">
                    {c.status}
                  </div>
                  {c.status === "archived" && <Archive size={14} className="text-[#7A7A7A]" />}
                  {c.status === "shared" && <Share2 size={14} className="text-[#1A422D]" />}
                </div>
                <div className="mt-2 font-serif text-xl text-[#1A422D]">{c.name}</div>
                <div className="mt-1 text-xs text-[#7A7A7A]">
                  {c.season || "—"} · {c.year || "—"} · {c.catalog_type || "General"}
                </div>
                <div className="mt-3 inline-flex items-center gap-2 text-xs text-[#4A4A4A]">
                  <FileText size={13} /> {c.product_count || 0} product{c.product_count === 1 ? "" : "s"}
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {showCreate && (
        <CreateCatalogModal
          types={catalogTypes}
          onClose={() => setShowCreate(false)}
          onCreated={(cat) => {
            setShowCreate(false);
            navigate(`/vendor/catalogs/${cat.catalog_id}`);
          }}
        />
      )}
    </VendorLayout>
  );
}

function CreateCatalogModal({ types, onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", season: "", year: "", description: "", catalog_type: types[0] });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    if (!form.name.trim() || form.name.trim().length < 2) {
      setError("Catalog name is required."); return;
    }
    setBusy(true); setError(null);
    try {
      const payload = { ...form, name: form.name.trim() };
      if (payload.year) payload.year = parseInt(payload.year, 10);
      Object.keys(payload).forEach((k) => { if (!payload[k]) delete payload[k]; });
      const { data } = await api.post("/vendor/catalogs", payload);
      onCreated?.(data);
    } catch (err) {
      setError(formatApiErrorDetail(err) || "Could not create catalog.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="create-catalog-modal">
      <div className="bg-white w-full max-w-lg p-8 border border-[#E5E3D8]">
        <div className="overline">New Catalog</div>
        <h2 className="font-serif text-2xl text-[#1A422D] mt-1">Create Catalog</h2>

        <div className="mt-6 space-y-4">
          <div>
            <label className="label-elegant block mb-1">Catalog Name</label>
            <input
              type="text" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Spring 2027 Collection"
              data-testid="new-catalog-name" className="input-field"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-elegant block mb-1">Season</label>
              <input type="text" value={form.season} onChange={(e) => setForm({ ...form, season: e.target.value })} data-testid="new-catalog-season" className="input-field" placeholder="Spring" />
            </div>
            <div>
              <label className="label-elegant block mb-1">Year</label>
              <input type="number" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} data-testid="new-catalog-year" className="input-field" placeholder="2027" />
            </div>
          </div>
          <div>
            <label className="label-elegant block mb-1">Catalog Type</label>
            <select value={form.catalog_type} onChange={(e) => setForm({ ...form, catalog_type: e.target.value })} data-testid="new-catalog-type" className="input-field">
              {types.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="label-elegant block mb-1">Description</label>
            <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="new-catalog-desc" className="input-field" />
          </div>
        </div>

        {error && <div className="mt-4 text-xs text-[#a33]" data-testid="new-catalog-error">{error}</div>}

        <div className="mt-6 flex items-center justify-end gap-3">
          <button type="button" onClick={onClose} data-testid="new-catalog-cancel" className="btn-secondary text-xs">Cancel</button>
          <button type="button" onClick={submit} disabled={busy} data-testid="new-catalog-submit" className="btn-primary">
            {busy ? "Creating…" : "Create Catalog"}
          </button>
        </div>
      </div>
    </div>
  );
}
